import { Component, Input, OnInit, computed, effect, model, signal, untracked } from '@angular/core'
import { toObservable, toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { SearchEntityType, SearchRequestType } from '@ftr/contracts/api/search'
import { Configuration } from '@ftr/contracts/type/court'
import { Uuid } from '@ftr/contracts/type/shared'
import { DestroySubscribers, RemoteData, isNotNullOrUndefined, unwrapData } from '@ftr/foundation'
import { CoreConfigurationService } from '@ftr/ui-court-system'
import {
  FetchSearchEntityTypeToNumberOfMatchesAction,
  FetchSearchManyResultsAction,
  SearchFocusState,
  SearchManyState,
  SearchState,
  SetBackToSearchResultsFullUrlAction,
  SetSearchFocusStateAction,
  SetSearchResultsScrollPositionAction,
  SetSelectedSearchItemAction,
  isValidSearchEntities,
  isValidSearchTerm,
} from '@ftr/ui-search'
import { Store } from '@ngxs/store'
import { delay, filter, first, takeUntil } from 'rxjs'
import { SearchBarDisplayService } from '~app/core/header/search-container-legacy/search-bar-legacy/search-bar-display.service'
import { SEARCH_OVERLAY_SELECTOR } from '~app/features/search/search-results/global-search-results.component'
import { SearchResultViewModel } from '~app/features/search/search-results/search-results-list/search-results-list.component'
import { SearchParams } from '~app/pages/search.params'
import { WindowRefService } from '~app/services/window/window-ref.service'

@Component({
  selector: 'ftr-search-container',
  templateUrl: './search-container.component.html',
})
export class SearchContainerComponent extends DestroySubscribers implements OnInit {
  @Input() isMobile: boolean
  @Input() isTablet: boolean
  @Input() isDesktop: boolean
  courtSystemId = model.required<Uuid>()
  isSearching = model.required<boolean>()

  protected isSearching$ = toObservable(this.isSearching)
  readonly searchTerm = signal('')
  readonly entitiesToSearch = signal<SearchEntityType[]>([])
  protected readonly searchFocusState = toSignal(this.store.select(SearchState.searchFocusState).pipe(delay(0)), {
    initialValue: SearchFocusState.NONE,
  })
  private readonly pageToken = signal<string | null>(null)
  //TODO: delay because these drive effects, and the subscribe could run in an effect context which causes a signal write
  // Should find a better toSignal from store, as store is definitely initialized, so this will always be the case
  protected readonly searchResponse = toSignal(this.store.select(SearchManyState.searchResponse).pipe(delay(0)), {
    initialValue: RemoteData.notAsked(),
  })

  protected readonly searchEntityTypeToNumberOfMatches = toSignal(
    this.store.select(SearchManyState.searchEntityTypeToNumberOfMatches).pipe(delay(0)),
    {
      initialValue: new Map<SearchEntityType, number>(),
    },
  )

  protected configuration = signal<Configuration | undefined>(undefined)

  // TODO this should come from a non legacy service ST-3252
  private searchRequestTypeOptions = toSignal(this.searchBarDisplayService.searchTypeOptions$, { initialValue: [] })
  protected availableEntities = computed(() => {
    const options = this.searchRequestTypeOptions()
    return options
      .map(x => x.value)
      .map(toSearchEntityType)
      .filter(isNotNullOrUndefined)
  })

  constructor(
    private readonly store: Store,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly coreConfigurationService: CoreConfigurationService,
    private readonly windowRefService: WindowRefService,
    private readonly searchBarDisplayService: SearchBarDisplayService,
  ) {
    super()
    this.keepUrlInSync()
    this.searchOnChange()
    effect(
      () => {
        const courtSystemId = this.courtSystemId()
        this.coreConfigurationService
          .getByCourtSystem(courtSystemId)
          .pipe(unwrapData(), takeUntil(this.finalize))
          .subscribe(configuration => {
            this.configuration.set(configuration)
          })
      },
      { allowSignalWrites: true },
    )
  }

  ngOnInit(): void {
    this.watchSearchParams()
  }

  private searchOnChange(): void {
    effect(
      () => {
        const searchTerm = this.searchTerm()
        const availableEntities = untracked(this.availableEntities)
        const courtSystemId = untracked(this.courtSystemId)

        this.entitiesToSearch.set([])

        this.store.dispatch(
          new FetchSearchEntityTypeToNumberOfMatchesAction(searchTerm, availableEntities, courtSystemId),
        )
      },
      { allowSignalWrites: true },
    )
    effect(() => {
      this.fetchResults()
    })
  }

  private keepUrlInSync(): void {
    effect(() => {
      const searchTerm = this.searchTerm()
      const pageToken = this.pageToken()
      const entitiesToSearch = this.entitiesToSearch()
      if (this.isSearching()) {
        if (searchTerm)
          this.router.navigate([], {
            queryParams: {
              [SearchParams.EntitiesToSearch]: entitiesToSearch,
              [SearchParams.SearchTerm]: searchTerm,
              [SearchParams.SearchPageToken]: pageToken,
            },
            queryParamsHandling: 'merge',
          })
        else {
          this.router.navigate([], {
            queryParams: {
              [SearchParams.EntitiesToSearch]: undefined,
              [SearchParams.SearchTerm]: undefined,
              [SearchParams.SearchPageToken]: undefined,
            },
            queryParamsHandling: 'merge',
          })
        }
      }
    })
  }

  protected onFilterItemSelected(type: SearchEntityType): void {
    this.entitiesToSearch.update(entities => {
      if (entities.includes(type)) return entities.filter(e => e !== type)
      return [...entities, type]
    })
  }

  onNewSearchFocusState(newState: SearchFocusState): void {
    this.store.dispatch(new SetSearchFocusStateAction(newState))
  }

  onRetry(): void {
    this.fetchResults()
  }

  onItemSelected(data: SearchResultViewModel): void {
    if (!data.link) {
      return
    }
    this.store.dispatch(
      new SetSearchResultsScrollPositionAction(this.windowRefService.getElementScrollPosition(SEARCH_OVERLAY_SELECTOR)),
    )
    this.store.dispatch(new SetSelectedSearchItemAction(data.content.body))
    const currentUrl = this.router.url
    this.store.dispatch(new SetBackToSearchResultsFullUrlAction(currentUrl))

    // Dispatch later to prevent the back to search appearing just before it navigates to the item.
    this.router.events
      .pipe(first(e => e instanceof NavigationEnd))
      .subscribe(() => this.store.dispatch(new SetBackToSearchResultsFullUrlAction(currentUrl)))

    // Clicking the element will navigate automatically, however pressing enter does not, so we need to navigate manually.
    this.router.navigate(data.link, { queryParams: data.queryParams })
  }

  private fetchResults(): void {
    const searchTerm = this.searchTerm()
    let entitiesToSearch = this.entitiesToSearch()
    const pageToken = this.pageToken()
    const courtSystemId = this.courtSystemId()
    if (searchTerm) {
      if (entitiesToSearch.length === 0) {
        entitiesToSearch = [SearchEntityType.Recordings, SearchEntityType.Orders]
      }
      this.store.dispatch(new SetSearchResultsScrollPositionAction(0))
      this.store.dispatch(new FetchSearchManyResultsAction(searchTerm, entitiesToSearch, courtSystemId, pageToken))
    }
  }

  /**
   * Handles the state of the search bar
   */
  private watchSearchParams(): void {
    this.route.queryParams
      .pipe(
        takeUntil(this.finalize),
        filter(p => p[SearchParams.SearchTerm] && isValidSearchTerm(p[SearchParams.SearchTerm])),
      )
      .subscribe(p => this.searchTerm.set(p[SearchParams.SearchTerm].trim()))

    this.route.queryParams
      .pipe(
        takeUntil(this.finalize),
        filter(p => p[SearchParams.EntitiesToSearch] && isValidSearchEntities(p[SearchParams.EntitiesToSearch])),
      )
      .subscribe(p => this.entitiesToSearch.set(p[SearchParams.EntitiesToSearch]))

    this.route.queryParams
      .pipe(
        takeUntil(this.finalize),
        filter(p => !p[SearchParams.SearchTerm]),
      )
      .subscribe(() => this.searchTerm.set(''))
  }
}

function toSearchEntityType(type: SearchRequestType): SearchEntityType | undefined {
  switch (type) {
    case SearchRequestType.AllRecordings:
      return SearchEntityType.Recordings
    case SearchRequestType.Orders:
      return SearchEntityType.Orders
    default:
      return undefined
  }
}
